// Drawers section in CabinetEditor.
//
// Renders the list of drawers nested under the selected cabinet, with
// per-drawer height + Blum slide-depth picker + Y position. The width
// of each drawer is implicit (cabinet width minus carcass + slide
// allowance) and read-only — that's the whole point of the constructor.
//
// New drawer ids are local to the cabinet (max(existing) + 1) so they
// don't have to thread through Project._nextLocalId; the global
// selection set only ever holds cabinet ids.

import { useMemo } from "react";
import type { Cabinet, Drawer } from "@/lib/types";
import { BLUM_SLIDE_DEPTHS, DEFAULT_SLIDE_DEPTH_MM, suggestDrawerY } from "@/lib/cabinets/drawers";

interface Props {
  cabinet: Cabinet;
  onChange: (changes: Partial<Cabinet>) => void;
}

export function DrawersSection({ cabinet, onChange }: Props) {
  const drawers = cabinet.drawers ?? [];

  // Drawer interior width is a function of the cabinet — shown read-only
  // so users see what the box will end up.
  const interiorWidth = useMemo(() => {
    const T = 0.75;
    const slideAllow = 0.5;
    return Math.max(0, cabinet.width - 2 * T - 2 * slideAllow);
  }, [cabinet.width]);

  function nextId(): number {
    if (drawers.length === 0) return 1;
    return Math.max(...drawers.map((d) => d._id)) + 1;
  }

  function add() {
    const height = Math.min(6, Math.max(3, cabinet.height / 4));
    const newDrawer: Drawer = {
      _id: nextId(),
      height,
      slideDepthMm: DEFAULT_SLIDE_DEPTH_MM,
      y: suggestDrawerY(cabinet, height),
      label: `Drawer ${drawers.length + 1}`,
    };
    onChange({ drawers: [...drawers, newDrawer] });
  }

  function update(id: number, patch: Partial<Drawer>) {
    const next = drawers.map((d) => (d._id === id ? { ...d, ...patch } : d));
    onChange({ drawers: next });
  }

  function remove(id: number) {
    const next = drawers.filter((d) => d._id !== id);
    onChange({ drawers: next.length ? next : undefined });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="label">Drawers</div>
        <button
          className="ghost"
          style={{ fontSize: 11, padding: "2px 8px" }}
          onClick={add}
          title={`New drawer (interior ≈ ${interiorWidth.toFixed(2)}″ wide)`}
        >+ Add</button>
      </div>

      {drawers.length === 0 ? (
        <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
          No drawers in this cabinet. Click <strong>+ Add</strong> to insert a
          drawer (interior width auto = {interiorWidth.toFixed(2)}″).
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 6 }}>
          {drawers.map((d, i) => (
            <li
              key={d._id}
              style={{
                border: "1px solid var(--line)",
                borderRadius: 4,
                padding: 6,
                fontSize: 11,
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 4,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {d.label ?? `Drawer ${i + 1}`}
              </span>
              <button
                className="ghost"
                style={{ fontSize: 10, padding: "0 6px", color: "var(--danger)" }}
                onClick={() => remove(d._id)}
                title="Delete drawer"
              >×</button>

              <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 6, rowGap: 4, alignItems: "center" }}>
                <span className="muted">Height ″</span>
                <input
                  type="number" step={0.25} min={2} max={24}
                  value={d.height}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v > 0) update(d._id, { height: v });
                  }}
                  style={{ width: "100%", fontSize: 11 }}
                />

                <span className="muted">Y ″</span>
                <input
                  type="number" step={0.25} min={0}
                  value={d.y}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= 0) update(d._id, { y: v });
                  }}
                  style={{ width: "100%", fontSize: 11 }}
                />

                <span className="muted">Slide</span>
                <select
                  value={d.slideDepthMm}
                  onChange={(e) => update(d._id, { slideDepthMm: Number(e.target.value) })}
                  style={{ width: "100%", fontSize: 11 }}
                >
                  {BLUM_SLIDE_DEPTHS.map((opt) => (
                    <option key={opt.depthMm} value={opt.depthMm}>{opt.label}</option>
                  ))}
                </select>

                <span className="muted">Width</span>
                <span style={{ fontSize: 11, color: "var(--fg-dim)" }}>
                  {interiorWidth.toFixed(2)}″ (auto)
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
